import { CreativeType, type Creative } from '../types'

/**
 * A suggestion generation runs ~90s per attempt × up to 3 attempts (~4.5min
 * worst case). Past this margin a card still showing no media and a
 * non-terminal status is almost certainly orphaned — e.g. a fire-and-forget
 * agent loop killed mid-flight by a deploy — so its Pusher completion event
 * will never arrive. Treat it as failed so the card offers Retry instead of
 * shimmering forever. Kept comfortably above the worst-case generation time so
 * a legitimately-slow (retrying) card is never flipped prematurely.
 */
export const STALE_GENERATION_MS = 8 * 60 * 1000

/**
 * X is the one text-first platform: a tweet with no image is a finished post.
 * Treat its text as the renderable asset so a text-only X post is neither
 * "still creating" (PostModal / arrival page) nor a failed suggestion
 * (Home/Posts auto-retry) nor an eternal skeleton (PostModal preview pane).
 * Whitespace-only text does not count.
 */
export function xPostHasText(creative: Creative | null): boolean {
  if (!creative || creative.type !== CreativeType.XPost) return false
  const content = creative.content as unknown as Record<string, unknown> | null | undefined
  // A sibling-derived X row can carry the master's `caption` instead of `text`;
  // the server publishes that caption (normalizeXContent), so it counts here too.
  const text =
    typeof content?.text === 'string' && content.text.trim() ? content.text : content?.caption
  return typeof text === 'string' && text.trim().length > 0
}

/**
 * True when a creative carries something AssetMedia can actually render —
 * a single image, a video, or at least one carousel slide. Mirrors the
 * branches in AssetMedia.extractMedia / the previewSiblings cache check so
 * "has media" means the same thing everywhere.
 */
export function creativeHasRenderableMedia(creative: Creative | null): boolean {
  if (!creative) return false
  const content = creative.content as unknown as Record<string, unknown> | null | undefined
  if (!content) return false
  if (content.imageUrl || content.videoUrl) return true
  if (xPostHasText(creative)) return true
  // A blueprint-first reel DRAFT: generation flips 'ready' at the blueprint (editable
  // storyboard), while the mp4 arrives minutes later via hydration. It has no videoUrl
  // yet BY DESIGN — treating it as empty would flag a healthy draft as failed and the
  // Home/Posts auto-retry would regenerate it mid-hydration. SCOPED to drafts (no
  // videoStatus): the legacy FAL path also stamps format:'reel' at generation start,
  // and its orphan/failure detection (videoStatus present) must keep working.
  if (!content.videoStatus && (content.videoHydration || content.format === 'reel')) return true
  const items = content.mediaItems as unknown[] | undefined
  return Array.isArray(items) && items.length > 0
}

/**
 * True when a suggestion card has terminally failed to produce media. Three
 * ways that happens:
 *   - the server explicitly marked it `'failed'`;
 *   - it finished (`'ready'`) but carries no usable media — e.g. a carousel
 *     marked ready on caption alone with zero slides. "Ready" means generation
 *     is done, so an empty ready card will never fill in; it's terminal;
 *   - it's been stuck `'generating'` (and still has no media) well past any
 *     real generation window, i.e. orphaned.
 *
 * A creative that already has renderable media is never failed, so this is safe
 * to call on every idea (not just the no-media render paths) — the auto-retry
 * on Home/Posts relies on that to avoid regenerating healthy cards.
 *
 * Shared by AssetMedia (renders the Retry affordance) and HomePage/PostsPage
 * (auto-fire the same retry on page load) so they all agree on "failed".
 */
export function isFailedSuggestion(creative: Creative | null): boolean {
  if (!creative) return false
  if (creative.suggestionStatus === 'failed') return true
  // A creative that can render something is never failed — short-circuit before
  // the no-media reasoning below so this is safe to call on every idea.
  if (creativeHasRenderableMedia(creative)) return false

  // From here on the creative has no renderable media.
  // An SKv2 reel actively in its video pipeline — hydration filming beats
  // (videoHydration marker) or the charged master render (videoLifeCharged +
  // videoStatus 'generating') — is WORKING, not failed: its suggestionStatus
  // 'ready' only means the blueprint draft shipped. The stuck-reel reclaim
  // sweep settles a wedged pipeline server-side, so this never hides a true
  // orphan forever. (The legacy FAL path writes neither marker, so its
  // orphan detection below keeps working.)
  const pipelineContent = creative.content as unknown as Record<string, unknown> | null | undefined
  if (
    pipelineContent?.videoHydration ||
    (pipelineContent?.videoLifeCharged && pipelineContent?.videoStatus === 'generating') ||
    // An SKv2 FULL reel render (pinned-photo reels skip the blueprint draft):
    // the server stamps engine fields + videoStatus at render start and the run
    // takes 12-18 min, well past the stale window. Scoped to v2 so the FAL
    // path's bare-videoStatus orphan detection below keeps working; the
    // stuck-reel sweep settles a true v2 orphan server-side.
    (pipelineContent?.videoStatus === 'generating' &&
      pipelineContent?.generationEngineVersion === 'v2')
  ) {
    return false
  }
  // Finished generating yet empty (e.g. a carousel marked ready on caption
  // alone, no slides) — terminal, it will never fill in.
  if (creative.suggestionStatus === 'ready') return true
  // Stuck 'generating', or a legacy suggestion whose status was never recorded
  // (`undefined`), that's been empty well past the worst-case generation window
  // — orphaned, its completion event will never arrive.
  if (creative.suggestionStatus === 'generating' || creative.isSuggestion) {
    const created = new Date(creative.createdAt).getTime()
    return Number.isFinite(created) && Date.now() - created > STALE_GENERATION_MS
  }
  return false
}

/**
 * True while Taka is still actively MAKING this post: the suggestion agent is
 * mid-generation, or a blueprint reel is mid-flight (hydration filming beats, or the
 * final master render — `videoHydration` progress marker / `videoStatus:'generating'`
 * with no mp4 yet). Deliberately NOT just `format==='reel' && !videoUrl`: that shape is
 * also the terminal state of a parked (out-of-credits) or failed-then-settled reel, and
 * claiming "making it now" on those would be a lie. Failed/stale suggestions are
 * excluded via isFailedSuggestion (which stale-bounds a wedged 'generating').
 */
export function isStillMaking(creative: Creative | null): boolean {
  if (!creative || isFailedSuggestion(creative)) return false
  if (creative.suggestionStatus === 'generating') return true
  const content = creative.content as unknown as Record<string, unknown> | null | undefined
  if (!content || content.videoUrl) return false
  return !!content.videoHydration || content.videoStatus === 'generating'
}
